// Lib
import { useCallback, useEffect, useState, useMemo } from "react";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

// Local
import ReportBuilderLayout from "../../components/ReportBuilderLayout";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { DbRead, DbWrite } from "../../database";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { useSearchParams } from "react-router-dom";
import { CustomDateInput } from "../../components/DatePickerWithManualEntry";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import * as strings from "../../strings";
import { KpiCategoryType, KpiDocument, KpiSpec } from "../../models/kpis";
import {
  getCategoryName,
  groupKpiDataByDay,
  groupKpiDataByMonth,
  groupKpiDataByWeek,
  KpiDataPoint,
  transformPieChartDataByCategory,
} from "../../utils/kpiUtils";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import KpiBarChart from "../../components/KPIs/BarChart";
import KpiDonutChart from "../../components/KPIs/DonutChart";
import moment from "moment";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import KpiDataTable, {
  KPITableViewMode,
} from "../../components/KPIs/DataTable";
import { USER_KPIS_URL } from "../../urls";
import SearchableDropdown from "../../components/SearchableDropdown";

interface Props {
  siteKey: string;
}

function groupLocationsByDepartment(locations: ExistingSiteKeyLocation[]) {
  const departments = new Set(locations.map((loc) => loc.department));
  const grouped = Array.from(departments).map((dept) => ({
    department: dept,
    locations: locations.filter((loc) => loc.department === dept),
  }));
  return grouped.sort((a, b) => a.department.localeCompare(b.department));
}

/**
 * Handles fetching the schema and displaying charts for premade reports.
 */
export default function KPIsContainer(props: Props) {
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const navigate = useNavigate();

  const userDisplayNamesMap =
    useUserDisplayNamesStore((state) => state.userDisplayNames) || {};
  const siteKeyLocations =
    useSiteKeyLocationsStore((state) => state.siteKeyLocationList) || [];

  const [groupByDate, setGrouByDate] = useState<string>("month");

  // Used for sending start and end dates as url parameters to the backend.
  // example: /api/reports/invoice-weekly-sales?start=2021-01-01&end=2021-12-31
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [isRecalculatingData, setIsRecalculatingData] =
    useState<boolean>(false);
  const [kpiCategoryType, setKpiCategoryType] =
    useState<KpiCategoryType | null>(null);

  const [tableViewMode, setTableViewMode] = useState<KPITableViewMode>("date");

  // Add new state for location filtering
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  // Add new state for storing the raw firestore data
  const [firestoreData, setFirestoreData] = useState<KpiDocument[]>([]);

  // Replace availableLocationIds with site key locations store
  const { siteKeyLocationList, fetch: fetchLocations } =
    useSiteKeyLocationsStore();

  // Fetch locations when component mounts
  useEffect(() => {
    fetchLocations(props.siteKey);
  }, [fetchLocations, props.siteKey]);

  useEffect(() => {
    async function getKpiSpecs() {
      setIsFetchingKpiSpecs(true);

      try {
        const kpiSpecs = await DbRead.kpisv2.getKpiSpecs(props.siteKey);
        setKpiSpecs(kpiSpecs);
        setIsFetchingKpiSpecs(false);
      } catch (e) {
        addMessage({
          id: createToastMessageID(),
          message: strings.ERROR_FETCHING_DATA,
          dialog: false,
          type: "error",
        });
        setIsFetchingKpiSpecs(false);
      }
    }

    getKpiSpecs();
  }, [addMessage, props.siteKey]);

  /**
   * Convenience function to add or update URL search parameters while
   * keeping the previous ones.
   * Replaces most recent browser history entry instead of adding
   * every incremental change to the URL search parameters.
   */
  const updateUrlSearchParams = useCallback(
    (newParams: Record<string, string>) => {
      setUrlSearchParams(
        (prev) => {
          const prevParams: Record<string, string> = {};
          prev.forEach((value, key) => {
            prevParams[key] = value;
          });

          return {
            ...prevParams,
            ...newParams,
          };
        },
        { replace: true },
      );
    },
    [setUrlSearchParams],
  );
  // const router =
  const UrlStartDate = urlSearchParams.get("startDate");
  const UrlEndDate = urlSearchParams.get("endDate");

  let startDate: string;
  let endDate: string;

  const defaultStartDate = DateTime.now().minus({ months: 1 }).toISODate();
  const defaultEndDate = DateTime.now().toISODate();
  if (!UrlStartDate || !UrlEndDate) {
    // Set defaults if not in url parameters.
    startDate = defaultStartDate;
    endDate = defaultEndDate;
    updateUrlSearchParams({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    });
  } else {
    // Parse into ISO Date or fall back to default.
    const dtStart = DateTime.fromFormat(UrlStartDate, "yyyy-MM-dd");
    startDate = dtStart.isValid ? dtStart.toISODate() : defaultStartDate;
    const dtEnd = DateTime.fromFormat(UrlEndDate, "yyyy-MM-dd");
    endDate = dtEnd.isValid ? dtEnd.toISODate() : defaultEndDate;
  }

  // #region ---- UI STATE ----
  const [selectedKpiSpec, setSelectedKpiSpec] = useState<string>("");
  const [kpiSpecs, setKpiSpecs] = useState<Record<string, KpiSpec>>({});
  const [isFetchingKpiSpecs, setIsFetchingKpiSpecs] = useState<boolean>(false);

  // #endregion

  // #region ---- COMPUTE UI OPTIONS ----
  const [rawData, setRawData] = useState<KpiDataPoint[]>([]);
  const [groupedDataByDate, setGroupedDataByDate] = useState<KpiDataPoint[]>(
    [],
  );
  const [groupedDataByCategory, setGroupedDataByCategory] = useState<
    KpiDataPoint[]
  >([]);
  const [groupedDataByDateAll, setGroupedDataByDateAll] = useState<
    KpiDataPoint[]
  >([]);
  const [categories, setCategories] = useState<string[]>([]);

  async function recalculateData() {
    setIsRecalculatingData(true);
    try {
      await DbWrite.kpis.recalculate(props.siteKey, null, startDate, endDate);
      setIsRecalculatingData(false);
    } catch (e) {
      addMessage({
        id: createToastMessageID(),
        message: strings.ERROR_RECALCULATING_DATA,
        dialog: false,
        type: "error",
      });
      setIsRecalculatingData(false);
    }
  }

  // Update the fetch function to remove location ID setting
  const fetchFirestoreData = useCallback(async () => {
    console.log("Fetching KPI documents from Firestore");
    try {
      if (selectedKpiSpec === "") {
        addMessage({
          id: createToastMessageID(),
          message: strings.KPI_NOT_SELECTED,
          dialog: false,
          type: "error",
        });
        return;
      }

      setIsFetchingData(true);
      const kpiDocs = await DbRead.kpisv2.getAllInDateRange(
        props.siteKey,
        selectedKpiSpec,
        startDate,
        endDate,
      );

      setKpiCategoryType(
        kpiDocs && kpiDocs.length > 0
          ? (kpiDocs[0].categoryType ?? null)
          : null,
      );
      setFirestoreData(kpiDocs || []);
      setIsFetchingData(false);
    } catch (error) {
      addMessage({
        id: createToastMessageID(),
        message: strings.ERROR_FETCHING_DATA,
        dialog: false,
        type: "error",
      });
      setIsFetchingData(false);
    }
  }, [selectedKpiSpec, props.siteKey, startDate, endDate, addMessage]);

  // Separate processing function
  const processData = useCallback(() => {
    if (
      !firestoreData ||
      !Array.isArray(firestoreData) ||
      firestoreData.length === 0
    ) {
      setCategories([]);
      setRawData([]);
      return;
    }

    try {
      const { categoryValues, allData } = getRawDataAndCategories({
        firestoreData,
        selectedLocationIds,
        startDate,
        endDate,
      });

      // Add "All" to the categories array only if there are multiple categories
      const categoriesWithAll = Array.from(categoryValues || []);
      if (categoriesWithAll.length > 1) {
        categoriesWithAll.push("All");
      }
      setCategories(categoriesWithAll);
      setRawData(allData || []);
    } catch (error) {
      console.error("Error processing KPI data:", error);
      setCategories([]);
      setRawData([]);
    }
  }, [firestoreData, selectedLocationIds, startDate, endDate]);

  // Add effect to process data when relevant values change
  useEffect(() => {
    processData();
  }, [processData]);

  useEffect(() => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return;
    }

    // Group the data first without the "All" category
    let processedData: KpiDataPoint[];

    if (groupByDate === "week") {
      processedData = groupKpiDataByWeek(rawData, false);
    } else if (groupByDate === "day") {
      processedData = groupKpiDataByDay(rawData, startDate, endDate, false);
    } else {
      processedData = groupKpiDataByMonth(rawData, startDate, endDate, false);
    }

    // Add "All" category to the processed data if there are multiple categories
    const categoryCount = categories.filter((cat) => cat !== "All").length;
    if (categoryCount > 1) {
      const processedDataWithAll = processedData.map((dataPoint) => {
        let allNumerator = 0;
        let allDenominator = 0;
        let allSum = 0;
        let hasNumeratorDenominator = false;

        // Check if this is a numerator/denominator KPI by looking at the first non-date value
        const firstValue = Object.values(dataPoint).find(
          (val) => val !== null && val !== undefined && typeof val !== "string",
        );
        if (
          typeof firstValue === "object" &&
          firstValue !== null &&
          firstValue !== undefined &&
          "numerator" in firstValue
        ) {
          hasNumeratorDenominator = true;
        }

        // Sum all categories to create "All" value
        Object.keys(dataPoint).forEach((key) => {
          if (key !== "date" && key !== "_sortKey") {
            const value = dataPoint[key];
            if (
              hasNumeratorDenominator &&
              typeof value === "object" &&
              value !== null &&
              value !== undefined &&
              "numerator" in value &&
              "denominator" in value
            ) {
              const valueObj = value as Record<string, number>;
              allNumerator += valueObj.numerator || 0;
              allDenominator += valueObj.denominator || 0;
            } else if (typeof value === "number" && !isNaN(value)) {
              allSum += value;
            }
          }
        });

        // Create the "All" value
        const allValue = hasNumeratorDenominator
          ? allNumerator / (allDenominator || 1) // Calculate the actual average for display
          : allSum;

        return {
          ...dataPoint,
          All: allValue,
        };
      });
      setGroupedDataByDate(processedDataWithAll);
    } else {
      setGroupedDataByDate(processedData);
    }

    // Set the "All" grouped data using the existing combineCategoriesToAll logic
    if (categoryCount > 1) {
      if (groupByDate === "week") {
        setGroupedDataByDateAll(groupKpiDataByWeek(rawData, true));
      } else if (groupByDate === "day") {
        setGroupedDataByDateAll(
          groupKpiDataByDay(rawData, startDate, endDate, true),
        );
      } else {
        setGroupedDataByDateAll(
          groupKpiDataByMonth(rawData, startDate, endDate, true),
        );
      }
    } else {
      setGroupedDataByDateAll([]);
    }
  }, [endDate, groupByDate, rawData, startDate, categories]);

  useEffect(() => {
    if (
      !rawData ||
      !Array.isArray(rawData) ||
      rawData.length === 0 ||
      !categories ||
      !Array.isArray(categories)
    ) {
      return;
    }
    const output: any[] = [];

    const categorySums: Record<string, any> = {};

    // categories is an array of userIDs, taskTypes, craftTypes, or other raw values
    for (const category of categories) {
      if (category && typeof category === "string") {
        for (const row of rawData) {
          if (row && row[category] !== undefined && row[category] !== null) {
            if (
              typeof row[category] === "object" &&
              row[category] !== null &&
              typeof (row[category] as Record<string, any>)["numerator"] ===
                "number" &&
              typeof (row[category] as Record<string, any>)["denominator"] ===
                "number"
            ) {
              const rowCategory = row[category] as Record<string, number>;
              if (!categorySums[category]) {
                categorySums[category] = {
                  numerator: rowCategory["numerator"],
                  denominator: rowCategory["denominator"],
                };
              } else {
                const existingCategory = categorySums[category] as Record<
                  string,
                  number
                >;
                existingCategory["numerator"] += rowCategory["numerator"];
                existingCategory["denominator"] += rowCategory["denominator"];
              }
            } else {
              // if categorySums has this categoryName already, then add the new sum to it, otherwise set it to the new sum
              if (!categorySums[category]) {
                categorySums[category] = Number(row[category]);
              } else {
                categorySums[category] += Number(row[category]);
              }
            }
          }
        }
      }
    }

    // output should be in format of [{category: "name", amount: 12}]
    for (const [category, amount] of Object.entries(categorySums)) {
      if (typeof amount === "object") {
        const amountObj = amount as Record<string, number>;
        let newAmount = 0;
        if (amountObj["denominator"] !== 0) {
          newAmount = amountObj["numerator"] / amountObj["denominator"];
        }
        output.push({
          date: getCategoryName(
            category,
            kpiCategoryType,
            siteKeyLocations,
            userDisplayNamesMap,
          ),
          [category]: newAmount,
          amount: newAmount,
        });
      } else {
        output.push({
          date: getCategoryName(
            category,
            kpiCategoryType,
            siteKeyLocations,
            userDisplayNamesMap,
          ),
          [category]: amount,
          amount: amount,
        });
      }
    }

    setGroupedDataByCategory(output.sort((a, b) => b.amount - a.amount));
  }, [categories, kpiCategoryType, rawData, userDisplayNamesMap]);
  // #endregion

  const locationGroups = useMemo(() => {
    return siteKeyLocationList.length > 4
      ? groupLocationsByDepartment(siteKeyLocationList)
      : null;
  }, [siteKeyLocationList]);

  return (
    <ReportBuilderLayout
      title="KPIs"
      titleAction={{
        label: "View User KPIs",
        onClick: () => navigate(USER_KPIS_URL),
        useSecondaryButton: true,
      }}
    >
      {{
        tableControlsSlot: (
          <>
            {/* Inputs */}
            <div className="-mt-2 flex justify-start gap-4 lg:items-center">
              <div className="flex max-w-2xl grow flex-col gap-4 lg:flex-row lg:items-center">
                {/* Report Select */}
                <div className="-mt-1 min-w-72 max-w-96 grow">
                  {isFetchingKpiSpecs && <LoadingSpinner />}
                  {!isFetchingKpiSpecs && (
                    <SearchableDropdown
                      items={Object.entries(kpiSpecs)
                        .sort((a, b) =>
                          a[1].reportTitle.localeCompare(b[1].reportTitle),
                        )
                        .map(([key, kpiSpec]) => ({
                          id: key,
                          title: kpiSpec.reportTitle,
                        }))}
                      value={selectedKpiSpec || ""}
                      onChange={(ev) => {
                        setRawData([]);
                        setCategories([]);
                        setGroupedDataByDate([]);
                        setGroupedDataByCategory([]);
                        setGroupedDataByDateAll([]);
                        setFirestoreData([]);
                        setKpiCategoryType(null);
                        setSelectedKpiSpec(ev);
                      }}
                      placeholder="Select Report"
                    />
                  )}
                </div>

                <div className="-mb-1 flex gap-4">
                  <FormControl sx={{ maxWidth: 140 }}>
                    <InputLabel id="start-date-label">Start Date</InputLabel>
                    <DatePicker
                      selected={DateTime.fromISO(startDate).toJSDate()}
                      onChange={(date) => {
                        const newDate = date
                          ? DateTime.fromJSDate(date).toISODate()
                          : "";
                        updateUrlSearchParams({ startDate: newDate });
                      }}
                      customInput={
                        <CustomDateInput
                          id="start-date"
                          className="pl-3 pt-1"
                        />
                      }
                    />
                  </FormControl>
                  <FormControl sx={{ maxWidth: 140 }}>
                    <InputLabel id="end-date-label">End Date</InputLabel>
                    <DatePicker
                      selected={DateTime.fromISO(endDate).toJSDate()}
                      onChange={(date) => {
                        const newDate = date
                          ? DateTime.fromJSDate(date).toISODate()
                          : "";
                        updateUrlSearchParams({ endDate: newDate });
                      }}
                      customInput={
                        <CustomDateInput id="end-date" className="pl-3 pt-1" />
                      }
                    />
                  </FormControl>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 lg:flex-nowrap">
                <BaseButtonPrimary
                  isBusy={isFetchingData}
                  busyText={strings.buttons.BUSY_LOADING}
                  onClick={fetchFirestoreData}
                >
                  {strings.buttons.SUBMIT}
                </BaseButtonPrimary>

                <BaseButtonPrimary
                  isBusy={isRecalculatingData}
                  busyText={strings.buttons.BUSY_LOADING}
                  onClick={() => {
                    recalculateData();
                  }}
                >
                  {strings.buttons.RECALCULATE}
                </BaseButtonPrimary>

                {/* Add Location Filter */}
                {siteKeyLocationList.length > 1 && firestoreData.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Locations</InputLabel>
                    <Select
                      multiple
                      value={selectedLocationIds}
                      label="Locations"
                      onChange={(event) => {
                        // Filter out any department values (they start with 'dept-')
                        const value = (event.target.value as string[]).filter(
                          (v) => !v.startsWith("dept-"),
                        );
                        setSelectedLocationIds(value);
                      }}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "All Locations"
                          : `${selected.length} selected`
                      }
                    >
                      {locationGroups
                        ? // Show departments when we have more than 4 locations
                          locationGroups
                            .map((group) => [
                              // Department header
                              <ListSubheader
                                key={`header-${group.department}`}
                                className="font-semibold"
                                sx={{ lineHeight: "32px", py: 0 }}
                              >
                                {group.department}
                              </ListSubheader>,
                              // Department selector
                              <MenuItem
                                key={`dept-${group.department}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const locationIds = group.locations.map(
                                    (loc) => loc.id,
                                  );
                                  const allSelected = group.locations.every(
                                    (loc) =>
                                      selectedLocationIds.includes(loc.id),
                                  );

                                  setSelectedLocationIds((prev) => {
                                    if (allSelected) {
                                      return prev.filter(
                                        (id) => !locationIds.includes(id),
                                      );
                                    } else {
                                      const newIds = [...prev];
                                      locationIds.forEach((id) => {
                                        if (!newIds.includes(id)) {
                                          newIds.push(id);
                                        }
                                      });
                                      return newIds;
                                    }
                                  });
                                }}
                                sx={{ py: 0.5 }}
                              >
                                <Checkbox
                                  checked={group.locations.every((loc) =>
                                    selectedLocationIds.includes(loc.id),
                                  )}
                                  indeterminate={
                                    group.locations.some((loc) =>
                                      selectedLocationIds.includes(loc.id),
                                    ) &&
                                    !group.locations.every((loc) =>
                                      selectedLocationIds.includes(loc.id),
                                    )
                                  }
                                  sx={{ p: 0.5 }}
                                />
                                <ListItemText
                                  primary="Select All"
                                  primaryTypographyProps={{
                                    sx: { fontSize: "0.875rem" },
                                  }}
                                />
                              </MenuItem>,
                              // Individual locations
                              ...group.locations.map((location) => (
                                <MenuItem
                                  key={location.id}
                                  value={location.id}
                                  sx={{ pl: 4, py: 0.5 }}
                                >
                                  <Checkbox
                                    checked={selectedLocationIds.includes(
                                      location.id,
                                    )}
                                    sx={{ p: 0.5 }}
                                  />
                                  <ListItemText
                                    primary={location.title}
                                    primaryTypographyProps={{
                                      sx: { fontSize: "0.875rem" },
                                    }}
                                  />
                                </MenuItem>
                              )),
                            ])
                            .flat()
                        : // Show simple location list when 4 or fewer locations
                          siteKeyLocationList.map((location) => (
                            <MenuItem
                              key={location.id}
                              value={location.id}
                              sx={{ py: 0.5 }}
                            >
                              <Checkbox
                                checked={selectedLocationIds.includes(
                                  location.id,
                                )}
                                sx={{ p: 0.5 }}
                              />
                              <ListItemText
                                primary={location.title}
                                primaryTypographyProps={{
                                  sx: { fontSize: "0.875rem" },
                                }}
                              />
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                )}
              </div>
            </div>
          </>
        ),
        tableSlot: (
          <>
            {rawData.length > 0 && (
              <>
                {(kpiSpecs[selectedKpiSpec].chartTypes.includes(
                  "stackedBarChart",
                ) ||
                  kpiSpecs[selectedKpiSpec].chartTypes.includes(
                    "barChart",
                  )) && (
                  <KpiBarChart
                    // key={`${groupedDataByDate.length}-${groupedDataByDateSecondary.length}`}
                    categoryType={kpiCategoryType}
                    categories={categories}
                    data={groupedDataByDate}
                    title={kpiSpecs[selectedKpiSpec].header}
                    subTitle={kpiSpecs[selectedKpiSpec].subheader}
                    formatting={kpiSpecs[selectedKpiSpec].formatting}
                    groupByDate={groupByDate}
                    handleGroupByDateChange={(value: string) =>
                      setGrouByDate(value)
                    }
                    stacked={kpiSpecs[selectedKpiSpec].chartTypes.includes(
                      "stackedBarChart",
                    )}
                    kpiTypes={Object.keys(kpiSpecs)}
                  />
                )}
                {kpiSpecs[selectedKpiSpec].chartTypes.includes(
                  "barChartForAll",
                ) && (
                  <KpiBarChart
                    categoryType={kpiCategoryType}
                    categories={["All"]}
                    data={groupedDataByDateAll}
                    title={kpiSpecs[selectedKpiSpec].header}
                    subTitle={kpiSpecs[selectedKpiSpec].subheader}
                    formatting={kpiSpecs[selectedKpiSpec].formatting}
                    groupByDate={groupByDate}
                    handleGroupByDateChange={(value: string) =>
                      setGrouByDate(value)
                    }
                    stacked={false}
                    kpiTypes={Object.keys(kpiSpecs)}
                  />
                )}
                {/* Show if any of the chartTypes of thr selected kpiSpec include and of the donutChart chartTypes */}
                {(kpiSpecs[selectedKpiSpec].chartTypes.includes(
                  "donutChartWithTotal",
                ) ||
                  kpiSpecs[selectedKpiSpec].chartTypes.includes(
                    "donutChartWithAverage",
                  ) ||
                  kpiSpecs[selectedKpiSpec].chartTypes.includes(
                    "donutChartWithTotalAndAverage",
                  )) && (
                  <KpiDonutChart
                    categories={categories.filter((cat) => cat !== "All")}
                    data={transformPieChartDataByCategory(
                      groupedDataByDate,
                      categories.filter((cat) => cat !== "All"),
                      siteKeyLocations,
                      userDisplayNamesMap,
                      kpiCategoryType,
                    )}
                    kpiSpec={kpiSpecs[selectedKpiSpec]}
                  />
                )}
                {kpiSpecs[selectedKpiSpec].chartTypes.includes(
                  "barChartByCategory",
                ) && (
                  <KpiBarChart
                    categoryType={kpiCategoryType}
                    categories={categories.filter((cat) => cat !== "All")}
                    data={groupedDataByCategory}
                    title={kpiSpecs[selectedKpiSpec].header}
                    subTitle={kpiSpecs[selectedKpiSpec].subheader}
                    formatting={kpiSpecs[selectedKpiSpec].formatting}
                    groupByDate={groupByDate}
                    handleGroupByDateChange={null}
                    stacked={true}
                    kpiTypes={Object.keys(kpiSpecs)}
                  />
                )}

                {/* Add the data table */}
                <div className="col-span-2">
                  <KpiDataTable
                    userKPIs={false}
                    setTableViewMode={(mode: KPITableViewMode) => {
                      setTableViewMode(mode);
                    }}
                    tableViewMode={tableViewMode}
                    data={
                      tableViewMode === "date"
                        ? groupedDataByDate
                        : groupedDataByCategory
                    }
                    categories={categories}
                    formatting={kpiSpecs[selectedKpiSpec].formatting}
                    categoryType={kpiCategoryType}
                    userDisplayNamesMap={userDisplayNamesMap}
                    kpiTitle={kpiSpecs[selectedKpiSpec].reportTitle}
                    dateRange={{
                      startDate: startDate,
                      endDate: endDate,
                    }}
                  />
                </div>
              </>
            )}
          </>
        ),
      }}
    </ReportBuilderLayout>
  );
}

/**
 * Sorts and filters the KPI docs from firestore based on the selected dates.
 * Creates a unique set of categories from the data.
 * @param args - The arguments object containing the firestore data, selected location IDs, start date, and end date
 * @returns An object containing the category values and all data
 */
export function getRawDataAndCategories(args: {
  firestoreData: KpiDocument[];
  selectedLocationIds: string[];
  startDate: string;
  endDate: string;
}): { categoryValues: Set<string>; allData: KpiDataPoint[] } {
  const { firestoreData, selectedLocationIds, startDate, endDate } = args;

  let allData: KpiDataPoint[] = [];
  for (const doc of firestoreData) {
    if (doc.dataByLocationID && selectedLocationIds.length > 0) {
      // Filter and combine data from selected locations
      const locationData = selectedLocationIds.flatMap(
        (locationId) => doc.dataByLocationID?.[locationId] ?? [],
      ) as KpiDataPoint[];
      allData = allData.concat(locationData);
    } else {
      // Use regular data if no locations selected or no location data available
      allData = allData.concat(doc.data as KpiDataPoint[]);
    }
  }

  // Sort and filter by date
  allData = allData
    .sort((a, b) => a.date.localeCompare(b.date))
    .filter((data) => {
      return (
        moment(data.date).isSameOrAfter(moment(startDate)) &&
        moment(data.date).isSameOrBefore(endDate)
      );
    });

  // Create a unique set of the category values
  const categoryValues = new Set<string>();
  for (const data of allData) {
    Object.keys(data).forEach((key) => {
      if (key !== "date") {
        categoryValues.add(key);
      }
    });
  }
  return { categoryValues, allData };
}
